/**
 * Owner / HR payroll workspace.
 *
 * Renders the new 7-stage pipeline introduced in Phase 1A.1:
 *
 *   draft ─run─▶ draft ─preview─▶ previewed ─approve─▶ approved ─lock─▶ locked ─send─▶ sent
 *                                           └reject─▶ draft (Owner only)
 *
 * Buttons gate themselves on the current status of the period so the
 * UI never offers an invalid transition.
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import {
  useRunPayroll,
  usePreviewPayroll,
  useApprovePayroll,
  useRejectPayroll,
  useUnlockPayroll,
  useLockPayroll,
  useSendSlips,
  useDeliveries,
  useRetryDelivery,
  useDataEntries,
  useUpsertAdjustmentEntry,
  useDeleteDataEntry,
  useSetHoursWorked,
  usePayrollRunDetail,
  useEmployees,
  type PayrollPreviewResult,
  type DataEntryRow,
  type ReadinessItem,
} from '../../hooks';
import { apiFetch, ApiError } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';
import { Modal } from '../../components/ui/Modal';
import { useConfirm } from '../../components/ui/confirm';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { ComplianceAlertList, type ComplianceItem } from '../../components/ui/ComplianceAlert';
import {
  Play, Eye, CheckCircle2, XCircle, Lock, Unlock, Send, Mail, AlertTriangle, RotateCw,
  MailCheck, MailX, MailWarning, Plus, Minus, Trash2, ChevronRight, Printer, Banknote,
} from 'lucide-react';
import type { PayrollStatus } from '@payroll/shared';
import { payBasisView, staleSummaryDate, summaryNewerThanRun, type EmpType, type PayBasis, type DaySummary } from '../../lib/pay-basis';

export interface PayrollRow {
  id: string;
  emp_code: string;
  base_pay: number;
  gross: number;
  deductions_total: number;
  net: number;
  status: PayrollStatus;
  tax_amount: number;
  sso_amount: number;
  /** fb#254 กองทุนสงเคราะห์ลูกจ้าง (ส่วนลูกจ้าง) — ร่างเก่า/ปิดตั้งค่า = 0 */
  welfare_fund_amount?: number;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  bank: string | null;
  bank_account_masked: string | null;
  /** fb#69 — แผนก/ตำแหน่งจากข้อมูลพนักงาน ใช้กรองตารางเพื่อปรับค่าคอม/รายการหักทีละกลุ่ม */
  department?: string | null;
  position?: string | null;
  employee_type: 'monthly' | 'daily' | 'hourly' | null;
  daily_rate: number | null;
  pay_basis?: PayBasis | null;
  summary?: DaySummary | null;
  scan_days?: number;
  offday_scan_days?: number;
  period_start?: string;
  period_end?: string;
  /** fb#630 โหมดเงินเดือนล้วนของรอบที่รันไว้ (ร่างก่อน fb#630 = ไม่มี) */
  run_salary_only?: boolean | null;
}

const TYPE_FILTERS: Array<{ key: 'all' | EmpType; label: string }> = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'monthly', label: 'รายเดือน' },
  { key: 'daily', label: 'รายวัน' },
  { key: 'hourly', label: 'รายชั่วโมง' },
];

/** fb#133 (TN 28 ก.ย. 2569) — ชื่อรายการปรับที่ใช้บ่อย ให้กดเลือกแทนพิมพ์ · เงินเพิ่มเลือกประเภทภาษีให้ด้วย
 *  ⚠️ ชื่อที่ตรงช่องในไฟล์ชีตต้องโดน conflict regex ของ lib/sheet-adjustments.ts (กันนำเข้าซ้ำ) — เช่น 'โบนัส' ไม่โดน /พิเศษ/ จึงไม่ใส่ */
const ADJ_LABEL_CHOICES: Record<'add' | 'deduct', Array<{ label: string; preset?: 'bonus' | 'wage_correction' | 'reimbursement' }>> = {
  add: [
    { label: 'ค่าคอมมิชชั่น', preset: 'wage_correction' },
    { label: 'ค่านายหน้า', preset: 'wage_correction' },
    { label: 'เงินพิเศษ', preset: 'bonus' },
    { label: 'ค่าจ้างตกหล่น', preset: 'wage_correction' },
    { label: 'เบิกคืนค่าใช้จ่าย', preset: 'reimbursement' },
  ],
  deduct: [
    { label: 'ค่าผิดพลาด' },
    { label: 'ค่าเสื้อบริษัท' },
    { label: 'ประกันร้าน' },
    { label: 'เบิกล่วงหน้า' },
    { label: 'กยศ.' }, // fb#512 — ใบสลิปจัดเข้าแถว "เงินกู้ยืม กยศ. / กรอ." เอง (slip-rows /กยศ|กรอ/)
  ],
};

/** "2026-09-02" → "2 ก.ย. 2569" */
function thDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function getLast6Periods(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const STAGE_ORDER: PayrollStatus[] = [
  'draft', 'previewed', 'approved', 'locked', 'sent',
];

const STATUS_LABEL: Record<PayrollStatus, string> = {
  draft: 'ร่าง',
  previewed: 'ตัวอย่าง',
  approved: 'อนุมัติแล้ว',
  locked: 'ล็อกแล้ว',
  pdf_ready: 'PDF พร้อม',
  sending: 'กำลังส่ง',
  partial_sent: 'ส่งบางส่วน',
  sent: 'ส่งแล้ว',
  voided: 'ยกเลิก',
};

// Map every status to how far it has progressed along STAGE_ORDER.
// Non-linear states (pdf_ready/sending/partial_sent) all sit at "locked".
function reachedStageIndex(s: PayrollStatus): number {
  const map: Record<PayrollStatus, number> = {
    draft: 0, previewed: 1, approved: 2,
    locked: 3, pdf_ready: 3, sending: 3, partial_sent: 3,
    sent: 4, voided: -1,
  };
  return map[s];
}

/** Content-only version used by the PayrollCycle hub. */
export function OwnerPayrollContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [preview, setPreview] = useState<PayrollPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);

  const runPayroll = useRunPayroll();
  const previewPayroll = usePreviewPayroll();
  const approvePayroll = useApprovePayroll();
  const rejectPayroll = useRejectPayroll();
  const unlockPayroll = useUnlockPayroll();
  const lockPayroll = useLockPayroll();
  const sendSlips = useSendSlips();
  // Only fetch deliveries when the period has moved past lock (otherwise
  // the rows haven't been inserted yet).
  const showDeliveries = rows.length > 0 &&
    ['locked', 'sending', 'partial_sent', 'sent'].includes(rows[0]!.status);
  const deliveries = useDeliveries(showDeliveries ? period : null);
  const retryDelivery = useRetryDelivery();
  const dataEntries = useDataEntries(period);
  const upsertAdj = useUpsertAdjustmentEntry();
  const removeEntry = useDeleteDataEntry();
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; first_name: string; last_name: string; nickname?: string | null }> | undefined };
  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name} ${e.last_name}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const nameOf = (code: string) => empName.get(code) ?? code;
  const [warnings, setWarnings] = useState<Array<{ emp_code: string; type: string; label: string }>>([]);
  // per-run override: ติ๊กเพื่อข้ามการลงเวลา/ลา/OT งวดนี้ (จ่ายเต็มตามฐาน).
  // ไม่ติ๊ก = ใช้โหมดที่ตั้งไว้ทั้งบริษัท.
  const [salaryOnlyRun, setSalaryOnlyRun] = useState(false);

  const role = useAuthStore((s) => s.profile?.role ?? 'OWNER');
  const soloMode = useAuthStore((s) => s.profile?.solo_mode ?? false);
  const isOwner = role === 'OWNER';
  // Print/send the approval request is HR's job; an owner only sees it when
  // they also do HR's prep (solo tenant). The owner's screen is approve + bank file.
  const canPrintRequest = !isOwner || soloMode;
  const confirm = useConfirm();
  const consequence = useConsequence();
  const toast = useToast();
  // Adjustment modal (replaces the old 4× window.prompt flow).
  const [adjFor, setAdjFor] = useState<string | null>(null);
  const [adjDir, setAdjDir] = useState<'add' | 'deduct'>('add');
  const [adjPreset, setAdjPreset] = useState<'bonus' | 'wage_correction' | 'reimbursement'>('bonus');
  const [adjLabel, setAdjLabel] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  // id ของรายการปรับเดิมที่กำลังแก้ไข (null = กำลังเพิ่มรายการใหม่). ตัว backend
  // ผูก "รายการเดิม" ด้วยชื่อ+ทิศทางอยู่แล้ว (delete+insert คีย์เดียวกัน = ทับ)
  // แต่ถ้าเปลี่ยนชื่อ/ทิศทางตอนแก้ไข ต้องลบแถวเดิมด้วย id ก่อน กันเหลือแถวซ้ำค้าง
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // ปลดล็อกงวด — OWNER เท่านั้น + ต้องพิมพ์เหตุผล (B6)
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  // fb#145 — ล็อกงวดทั้งที่มีเรื่องค้าง: server ตอบ 409 พร้อม readiness → ใส่เหตุผลแล้วยิงซ้ำ
  // (เดิมปุ่มนี้ไม่มีทางยืนยัน เห็นแค่ toast "ยืนยันพร้อมเหตุผลเพื่อปิด" แต่ไม่มีช่องให้ใส่)
  const [lockAck, setLockAck] = useState<{ period: string; items: ReadinessItem[] } | null>(null);
  const [lockAckReason, setLockAckReason] = useState('');
  // Per-employee detail drawer (click a table row).
  const [detailFor, setDetailFor] = useState<string | null>(null);
  // แยกดูตามประเภทการจ้าง (รายเดือน/รายวัน/รายชั่วโมง) — ตรวจวันทำงานกับสแกนทีละกลุ่ม
  const [typeFilter, setTypeFilter] = useState<'all' | EmpType>('all');
  const typeOf = (r: PayrollRow): EmpType => r.pay_basis?.employee_type ?? r.employee_type ?? 'monthly';
  const [deptFilter, setDeptFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const groupRows = filterByGroup(rows, deptFilter, positionFilter);
  const shownRows = typeFilter === 'all' ? groupRows : groupRows.filter((r) => typeOf(r) === typeFilter);
  const stale = staleSummaryDate(rows);
  const resummarized = summaryNewerThanRun(rows);

  const status: PayrollStatus | null = rows.length ? rows[0]!.status : null;

  async function loadRows() {
    setLoading(true);
    try {
      const data = await apiFetch<PayrollRow[]>(`/owner/payroll/run?period=${period}`);
      setRows(data);
      setPreview(null);
    } catch {
      setRows([]);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleRun() {
    const ok = await consequence({
      title: `คำนวณร่างเงินเดือน งวด ${period}`,
      intro: 'ระบบจะคำนวณร่างเงินเดือนจากเวลาเข้างาน เงินเดือน และรายการปรับล่าสุด',
      effects: [
        'เป็นแค่ "ร่าง" — ยังไม่จ่ายเงิน และยังไม่ส่งสลิป',
        'คุณตรวจดูทีละคนได้ก่อนส่งขออนุมัติ',
        'คำนวณใหม่ได้เรื่อยๆ จนกว่าจะล็อกงวด',
      ],
      confirmText: 'คำนวณร่าง',
    });
    if (!ok) return;
    try {
      const res = await runPayroll.mutateAsync({ period, salary_only: salaryOnlyRun || undefined }) as { warnings?: Array<{ emp_code: string; type: string; label: string }> };
      setWarnings(res.warnings ?? []);
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  // fb#630 — แก้รายการปรับ/ชั่วโมงตอนงวดยังเป็น "ร่าง" → คำนวณร่างใหม่ให้เองทันที ไม่ต้องกด "รันร่างใหม่"
  // ส่งขออนุมัติแล้ว (previewed) ไม่รันเอง — รันใหม่จะถอยเป็นร่างและต้องส่งขออนุมัติใหม่ ให้ HR กดเอง
  // แก้ติดกันหลายรายการ = ต่อคิว ไม่ยิงรันซ้อนกัน (รันซ้อนลบแถวของกันและกันได้)
  // ระหว่างรัน: ล็อกเลือกงวด/โหลด/ส่งขออนุมัติ (กันรันงวดผิด · กันส่งขออนุมัติแล้วโดนรันทับกลับเป็นร่าง — astra)
  const rerunQueue = useRef<{ running: boolean; again: boolean }>({ running: false, again: false });
  const [autoRerunning, setAutoRerunning] = useState(false);
  const qc = useQueryClient();
  // ร่างก่อน fb#630 ไม่ได้จำโหมดเงินเดือนล้วน → ไม่รันเอง (เดาโหมดผิด = คิดลา/ขาด/OT ทั้งงวดเปลี่ยน) ให้กด "รันร่างใหม่" ครั้งแรกเอง
  const runMode = rows[0]?.run_salary_only;
  const canAutoRerun = status === 'draft' && typeof runMode === 'boolean';
  async function rerunAfterEdit() {
    if (!canAutoRerun) return;
    if (rerunQueue.current.running) { rerunQueue.current.again = true; return; }
    rerunQueue.current.running = true;
    setAutoRerunning(true);
    const runPeriod = period;
    try {
      do {
        rerunQueue.current.again = false;
        const res = await runPayroll.mutateAsync({ period: runPeriod, salary_only: runMode }) as { warnings?: Array<{ emp_code: string; type: string; label: string }> };
        setWarnings(res.warnings ?? []);
        // โหลดตารางในวง (แก้ระหว่างโหลดก็ได้รันตาม) · โหลดล้ม = แจ้ง ไม่ขึ้นว่าอัปเดตแล้ว
        setRows(await apiFetch<PayrollRow[]>(`/owner/payroll/run?period=${runPeriod}`));
        setPreview(null);
        void qc.invalidateQueries({ queryKey: ['payroll-run-detail'] });
      } while (rerunQueue.current.again);
      toast.success('คำนวณร่างใหม่แล้ว — ตัวเลขในตารางอัปเดตแล้ว');
    } catch (e) {
      toast.error(`บันทึกรายการแล้ว แต่คำนวณร่างใหม่ไม่สำเร็จ — กด "รันร่างใหม่" อีกครั้ง (${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'})`);
    } finally {
      rerunQueue.current.running = false;
      setAutoRerunning(false);
    }
  }

  function openAdjustment(empCode: string) {
    setAdjFor(empCode);
    setAdjDir('add');
    setAdjPreset('bonus');
    setAdjLabel('');
    setAdjAmount('');
    setEditingEntryId(null);
  }

  function closeAdjustmentModal() {
    setAdjFor(null);
    setEditingEntryId(null);
  }

  // โหลดรายการปรับเดิมเข้าฟอร์มเพื่อแก้ไข (แทนการเพิ่มรายการใหม่)
  function startEditAdjustment(entry: DataEntryRow) {
    setAdjDir(entry.entry_type === 'adjustment_add' ? 'add' : 'deduct');
    setAdjPreset((entry.adjustment_preset as 'bonus' | 'wage_correction' | 'reimbursement' | null) ?? 'bonus');
    setAdjLabel(entry.adjustment_label ?? '');
    setAdjAmount(String(entry.amount));
    setEditingEntryId(entry.id);
  }

  async function deleteAdjustmentEntry(id: string) {
    if (!(await confirm({ title: 'ลบรายการนี้?', danger: true, confirmText: 'ลบ' }))) return;
    await removeEntry.mutateAsync(id);
    if (editingEntryId === id) {
      setAdjLabel('');
      setAdjAmount('');
      setEditingEntryId(null);
    }
    void rerunAfterEdit();
  }

  async function submitAdjustment() {
    const amount = Number(adjAmount.replace(/,/g, ''));
    if (!adjFor || !adjLabel.trim() || !Number.isFinite(amount) || amount <= 0) {
      toast.error('กรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    try {
      // กำลังแก้ไขรายการเดิม — ลบแถวเดิมด้วย id ก่อนเสมอ กันเหลือแถวซ้ำถ้าเปลี่ยน
      // ชื่อ/ทิศทาง (ถ้าชื่อ+ทิศทางเดิมไม่เปลี่ยน backend จะทับให้เองอยู่แล้ว)
      //
      // ⭐ เดิมบรรทัดนี้พึ่งสมมติฐานว่า "ลบซ้ำไม่มีผลเสีย" ซึ่งจริงตอน backend
      // ตอบ 200 ให้ทุกครั้ง. ตอนนี้ backend ตอบ 404 เมื่อไม่โดนแถวไหน (กันอาการ
      // "จอบอกลบแล้วแต่ของยังอยู่") สมมติฐานเดิมจึงใช้ไม่ได้อีก — ถ้าไม่ดักไว้
      // เคสนี้จะค้างถาวร: ลบสำเร็จ → POST บันทึกทับล้ม → กดบันทึกใหม่ → ลบซ้ำ
      // ติด 404 ก่อนถึง POST ทุกครั้ง แถวเดิมหายไปแล้วแต่บันทึกใหม่ไม่ได้เลย
      // 404 ตรงนี้แปลว่า "แถวนั้นไม่อยู่แล้ว" = สิ่งที่เราต้องการอยู่แล้ว เดินต่อได้
      if (editingEntryId) {
        try {
          await removeEntry.mutateAsync(editingEntryId);
        } catch (e) {
          if (!(e instanceof ApiError && e.status === 404)) throw e;
        }
        setEditingEntryId(null);
      }
      await upsertAdj.mutateAsync({
        period,
        emp_code: adjFor,
        direction: adjDir,
        label: adjLabel.trim(),
        amount,
        preset: adjDir === 'add' ? adjPreset : undefined,
      });
      toast.success(editingEntryId ? 'แก้ไขรายการแล้ว' : 'บันทึกรายการแล้ว');
      closeAdjustmentModal();
      void rerunAfterEdit();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handlePreview() {
    if (!isOwner) {
      if (!(await confirm({ title: 'ส่งขออนุมัติเงินเดือน', message: `งวด ${period} — ระบบจะแจ้งเจ้าของทาง LINE ให้อนุมัติ`, confirmText: 'ส่งขออนุมัติ' }))) return;
    }
    try {
      const result = await previewPayroll.mutateAsync({ period });
      setPreview(result);
      await loadRows();
      if (!isOwner) toast.success('ส่งขออนุมัติให้เจ้าของแล้ว');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handleDownloadBankFile() {
    const jwt = useAuthStore.getState().jwt;
    const apiBase = import.meta.env.VITE_API_BASE ?? '';
    try {
      const res = await fetch(`${apiBase}/owner/payroll/bank-transfer?period=${encodeURIComponent(period)}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank-transfer-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  function handlePrint() {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast.error('เบราว์เซอร์บล็อกการเปิดหน้าต่าง — อนุญาต popup แล้วลองใหม่'); return; }
    const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
    const today = new Date().toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' });
    // fb#254 — คอลัมน์กองทุนสงเคราะห์ลูกจ้างโผล่เฉพาะงวดที่มีการหัก
    const showWelfare = sum(rows, 'welfare_fund_amount') > 0;
    const body = rows.map((r, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td>${esc(nameOf(r.emp_code))}<div class="muted">${esc(r.emp_code)}</div></td>
      <td>${r.bank ? esc(r.bank) + (r.bank_account_masked ? ' ••' + esc(r.bank_account_masked) : '') : '-'}</td>
      <td class="r">${fmt(r.gross)}</td>
      <td class="r">${fmt(r.tax_amount)}</td>
      <td class="r">${fmt(r.sso_amount)}</td>
      ${showWelfare ? `<td class="r">${fmt(r.welfare_fund_amount ?? 0)}</td>` : ''}
      <td class="r">${fmt(r.deductions_total)}</td>
      <td class="r b">${fmt(r.net)}</td>
    </tr>`).join('');
    const html = `<!doctype html><html lang="th"><head><meta charset="utf-8">
    <title>ใบขออนุมัติเงินเดือน ${esc(period)}</title>
    <style>
      *{font-family:'Sarabun','Tahoma',sans-serif;box-sizing:border-box}
      body{margin:24px;color:#1f2937;font-size:13px}
      h1{font-size:18px;margin:0 0 2px}
      .meta{color:#6b7280;font-size:12px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse}
      th,td{padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:left;vertical-align:top}
      th{background:#f3f4f6;font-weight:600;font-size:12px}
      .r{text-align:right}.c{text-align:center}.b{font-weight:700}
      .muted{color:#9ca3af;font-size:10px}
      tfoot td{border-top:2px solid #9ca3af;font-weight:700;background:#f9fafb}
      .sign{display:flex;gap:60px;margin-top:48px}
      .sign div{flex:1;text-align:center;border-top:1px dotted #9ca3af;padding-top:6px;color:#6b7280}
      @media print{body{margin:0}}
    </style></head><body>
    <h1>ใบขออนุมัติจ่ายเงินเดือน</h1>
    <div class="meta">งวด ${esc(period)} · พนักงาน ${rows.length} คน · พิมพ์เมื่อ ${esc(today)}</div>
    <table>
      <thead><tr>
        <th class="c">#</th><th>พนักงาน</th><th>ธนาคาร / บัญชี</th>
        <th class="r">รายได้รวม</th><th class="r">ภาษี</th><th class="r">ปกส.</th>
        ${showWelfare ? '<th class="r">กสล.</th>' : ''}
        <th class="r">หักรวม</th><th class="r">โอนสุทธิ</th>
      </tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <td></td><td>รวม ${rows.length} คน</td><td></td>
        <td class="r">${fmt(sum(rows, 'gross'))}</td>
        <td class="r">${fmt(sum(rows, 'tax_amount'))}</td>
        <td class="r">${fmt(sum(rows, 'sso_amount'))}</td>
        ${showWelfare ? `<td class="r">${fmt(sum(rows, 'welfare_fund_amount'))}</td>` : ''}
        <td class="r">${fmt(sum(rows, 'deductions_total'))}</td>
        <td class="r">${fmt(sum(rows, 'net'))}</td>
      </tr></tfoot>
    </table>
    <div class="sign"><div>ผู้จัดทำ (ฝ่ายบุคคล)</div><div>ผู้อนุมัติ (เจ้าของ)</div></div>
    <script>window.onload=function(){window.print()}</script>
    </body></html>`;
    w.document.write(html);
    w.document.close();
  }

  async function handleApprove() {
    const netTotal = sum(rows, 'net');
    const ok = await consequence({
      title: `อนุมัติเงินเดือน งวด ${period}`,
      intro: 'อนุมัติแล้วถือว่าเจ้าของยืนยันยอดจ่ายนี้ — ขั้นต่อไปคือล็อกงวดและจ่ายจริง',
      lines: [
        { label: 'พนักงานในงวดนี้', value: `${rows.length} คน` },
        { label: 'ยอดโอนสุทธิรวม', value: `฿${netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, tone: 'money', emphasis: true },
      ],
      confirmText: 'อนุมัติ',
    });
    if (!ok) return;
    try {
      const r = await approvePayroll.mutateAsync({ period });
      toast.success(`อนุมัติแล้ว — โหมด ${r.mode}`);
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function submitReject() {
    if (!rejectReason.trim()) { toast.error('ระบุเหตุผลที่ปฏิเสธ'); return; }
    try {
      await rejectPayroll.mutateAsync({ period, reason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function submitUnlock() {
    if (!unlockReason.trim()) { toast.error('ระบุเหตุผลที่ปลดล็อก'); return; }
    try {
      const res = await unlockPayroll.mutateAsync({ period, reason: unlockReason.trim() });
      setUnlockOpen(false);
      setUnlockReason('');
      await loadRows();
      if (res.warning) toast.info(res.warning);
      else toast.success('ปลดล็อกงวดแล้ว — แก้ไขข้อมูลแล้วรันร่างใหม่ได้เลย');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  async function handleLock() {
    const netTotal = sum(rows, 'net');
    const ok = await consequence({
      title: `ล็อกงวดเงินเดือน ${period}`,
      intro: 'ล็อกแล้วยอดเงินเดือนงวดนี้จะแก้ไขไม่ได้อีก — ใช้เป็นยอดจ่ายจริง',
      lines: [
        { label: 'พนักงานในงวดนี้', value: `${rows.length} คน` },
        { label: 'ยอดโอนสุทธิรวม', value: `฿${netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, tone: 'money', emphasis: true },
      ],
      effects: ['หลังล็อกจะส่งสลิปและออกไฟล์โอนธนาคารได้'],
      danger: true,
      confirmText: 'ล็อกงวด',
    });
    if (!ok) return;
    await submitLock({ period });
  }

  async function submitLock(body: { period: string; acknowledge?: boolean; ack_reason?: string }) {
    try {
      const res = await lockPayroll.mutateAsync(body);
      setLockAck(null);
      setLockAckReason('');
      // #hr-delegation Phase 3 — HR ที่ระดับ pending: คำขอถูกส่งให้เจ้าของ (ยังไม่ปิดงวด)
      if (res && (res as { queued?: boolean }).queued) {
        toast.info((res as { message?: string }).message ?? 'ส่งคำขอปิดงวดให้เจ้าของอนุมัติแล้ว');
        return;
      }
      await loadRows();
      toast.success('ปิดงวดแล้ว');
    } catch (e) {
      // soft-block: 409 + details.readiness และไม่มี hardBlockers → เปิดกล่องใส่เหตุผล
      if (!body.acknowledge && e instanceof ApiError && e.status === 409) {
        const d = (e.details ?? {}) as { readiness?: { items?: ReadinessItem[] }; hardBlockers?: unknown };
        if (d.readiness && !d.hardBlockers) {
          const items = (d.readiness.items ?? []).filter((i) => i.affectsCycle && i.count > 0);
          setLockAck({ period: body.period, items });
          return;
        }
      }
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function submitLockAck() {
    if (!lockAckReason.trim()) { toast.error('ระบุเหตุผลที่ปิดงวดทั้งที่มีเรื่องค้าง'); return; }
    // ผูกกับงวดที่ถูกบล็อกจริง ไม่ใช่งวดที่เลือกอยู่ตอนนี้ (กันสลับงวดระหว่างรอแล้วล็อกผิดงวด)
    if (!lockAck || lockPayroll.isPending) return;
    await submitLock({ period: lockAck.period, acknowledge: true, ack_reason: lockAckReason.trim() });
  }

  async function handleSend() {
    const ok = await consequence({
      title: `ส่งสลิปงวด ${period}`,
      intro: 'ระบบจะส่งสลิปเงินเดือนให้พนักงานทาง LINE เท่านั้น — ทางอีเมลใช้ปุ่ม "ส่งสลิปทางอีเมล" แยกต่างหาก',
      lines: [{ label: 'จำนวนสลิปที่จะส่ง', value: `${rows.length} ใบ` }],
      effects: ['พนักงานจะได้รับแจ้งเตือนทันที', 'ส่งซ้ำได้ภายหลังถ้ามีคนตกหล่น'],
      confirmText: 'ส่งสลิปทางไลน์',
    });
    if (!ok) return;
    try {
      await sendSlips.mutateAsync({ period });
      await loadRows();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  return (
    <>
      <PayrollPipeline
        current={['locked', 'pdf_ready', 'sending', 'partial_sent', 'sent'].includes(status ?? '') ? 'slips' : 'payroll'}
        period={period}
      />
      <div className="card p-4 mb-4 space-y-3">
        <div>
          <label className="label">เลือกงวด</label>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setRows([]); setPreview(null); }}
            disabled={autoRerunning}
            className="input"
          >
            {getLast6Periods().map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <button onClick={loadRows} disabled={loading || autoRerunning} className="btn-secondary w-full">
          {loading ? 'กำลังโหลด...' : 'โหลดข้อมูล'}
        </button>
      </div>

      {/* Stage indicator */}
      {status && (
        <div className="card p-3 mb-4">
          <div className="text-xs text-gray-500 mb-2">สถานะงวดนี้</div>
          <div className="flex items-center gap-1 overflow-x-auto">
            {STAGE_ORDER.map((s, i) => {
              const reached = i <= reachedStageIndex(status);
              return (
                <div key={s} className="flex items-center gap-1 shrink-0">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                      reached ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className={`text-xs ${reached ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {STATUS_LABEL[s]}
                  </span>
                  {i < STAGE_ORDER.length - 1 && <div className="w-3 h-0.5 bg-gray-200" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Smart warnings from Run Draft — เตือนเชิงรุก (Non-HR UX §4) */}
      {warnings.length > 0 && (
        <div className="mb-4">
          <ComplianceAlertList
            items={warnings.slice(0, 10).map<ComplianceItem>((w) => {
              if (w.type === 'no_salary_base') {
                return {
                  level: 'block',
                  title: `${nameOf(w.emp_code)} — ยังไม่ตั้งฐานเงินเดือน`,
                  detail: 'พนักงานคนนี้ไม่ถูกคำนวณในงวดนี้ — ตั้งฐานเงินเดือนก่อนแล้วทำเงินเดือนใหม่',
                };
              }
              // fb#503 — เดิมทุกชนิดที่ไม่ใช่ no_salary_base ตกมาเป็น "ขาดรายการปรับ" (รายชั่วโมง/ไม่มีตารางกะ ก็ขึ้นแบบนั้น)
              if (w.type !== 'missing_allowance' && w.type !== 'missing_adjustment') {
                return {
                  level: w.type === 'no_roster_schedule' ? 'block' : 'warn',
                  title: `${nameOf(w.emp_code)} — ${w.type === 'no_roster_schedule' ? 'ยังไม่มีตารางกะ' : 'ตรวจชั่วโมงทำงาน'}`,
                  detail: w.label,
                };
              }
              return {
                level: 'warn',
                title: `${nameOf(w.emp_code)} — ${w.type === 'missing_allowance' ? 'ขาดเบี้ยเลี้ยง' : 'ขาดรายการปรับ'}`,
                detail: `งวดก่อนมีรายการ "${w.label}" แต่งวดนี้ไม่มี — ตรวจว่าตั้งใจตัดออกหรือลืม`,
              };
            })}
            summary={
              <p className="text-sm font-medium text-gray-700">
                มี {warnings.length} เรื่องต้องตรวจก่อนส่งขออนุมัติ
              </p>
            }
          />
          {warnings.length > 10 && (
            <p className="text-xs text-gray-400 mt-1">… และอีก {warnings.length - 10} รายการ</p>
          )}
        </div>
      )}

      {/* Approval-sheet header — the "ใบขออนุมัติ" summary the owner signs off */}
      {rows.length > 0 && status === 'previewed' && (() => {
        const netTotal = sum(rows, 'net');
        const prevNet = preview?.prev_totals.net_sum ?? 0;
        const deltaPct = prevNet > 0 ? ((netTotal - prevNet) / prevNet) * 100 : null;
        return (
          <div className="card p-4 mb-4 bg-gradient-to-br from-brand-50 to-white border-brand-100">
            <div className="text-xs text-brand-700 font-medium">ยอดโอนสุทธิรวมงวดนี้</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="text-2xl font-bold text-brand-900 font-display tabular-nums">
                ฿{netTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-gray-500">· พนักงาน {rows.length} คน</div>
              {deltaPct !== null && (
                <div className={`text-sm font-medium ${Math.abs(deltaPct) >= 20 ? 'text-amber-700' : 'text-gray-500'}`}>
                  {deltaPct > 0 ? '▲ +' : '▼ '}{deltaPct.toFixed(1)}% เทียบงวดก่อน
                </div>
              )}
            </div>
            <div className={`grid grid-cols-2 gap-2 mt-3 ${sum(rows, 'welfare_fund_amount') > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
              <Chip label="รายได้รวม" value={sum(rows, 'gross')} />
              <Chip label="ภาษีรวม" value={sum(rows, 'tax_amount')} />
              <Chip label="ประกันสังคมรวม" value={sum(rows, 'sso_amount')} />
              {sum(rows, 'welfare_fund_amount') > 0 && <Chip label="กองทุนสงเคราะห์ลูกจ้างรวม" value={sum(rows, 'welfare_fund_amount')} />}
              <Chip label="โอนสุทธิรวม" value={netTotal} accent />
            </div>
          </div>
        );
      })()}

      {/* Data-entry pane (visible from draft → previewed) */}
      {rows.length > 0 && (status === 'draft' || status === 'previewed') && (
        <div className="card p-3 mb-4">
          <div className="font-semibold mb-2 text-sm">รายการปรับเพิ่ม / หัก งวดนี้</div>
          {dataEntries.data && dataEntries.data.length > 0 ? (
            <div className="mb-2 rounded-md border border-gray-200 divide-y divide-gray-200 overflow-hidden">
              {dataEntries.data.map((e) => {
                const detail = e.entry_type === 'variable_allowance'
                  ? `เบี้ยเลี้ยงผันแปร · จำนวน ${e.qty}`
                  : `${e.entry_type === 'adjustment_add' ? '+' : '−'}${e.amount.toLocaleString('th-TH')} (${e.adjustment_label})`;
                return (
                  <div key={e.id} className="flex items-center gap-2 text-xs px-2 py-1.5 even:bg-gray-50 hover:bg-amber-50">
                    <span className="w-28 sm:w-48 shrink-0 truncate" title={nameOf(e.emp_code)}>{nameOf(e.emp_code)}</span>
                    <span className={`min-w-0 break-words ${e.entry_type === 'variable_allowance' ? '' : e.entry_type === 'adjustment_add' ? 'text-green-700' : 'text-red-700'}`}>
                      {detail}
                    </span>
                    <button
                      onClick={async () => {
                        if (await confirm({ title: 'ลบรายการนี้?', message: `${nameOf(e.emp_code)} · ${detail}`, danger: true, confirmText: 'ลบ' })) {
                          await removeEntry.mutateAsync(e.id);
                          void rerunAfterEdit();
                        }
                      }}
                      className="p-1 shrink-0 text-gray-400 hover:text-red-600"
                      aria-label={`ลบรายการของ ${nameOf(e.emp_code)}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic mb-2">ยังไม่มีรายการ — รันร่างแล้วดูตัวอย่างได้เลย</p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
            {rows.map((r) => (
              <button
                key={r.emp_code}
                onClick={() => openAdjustment(r.emp_code)}
                className="btn-secondary text-xs justify-start truncate"
              >
                <Plus className="w-3 h-3" />{nameOf(r.emp_code)}
              </button>
            ))}
          </div>
          <p className={`text-[11px] mt-2 ${autoRerunning ? 'text-brand-700 font-medium' : 'text-gray-500'}`}>
            {autoRerunning
              ? 'กำลังคำนวณร่างใหม่ตามรายการที่แก้…'
              : canAutoRerun
                ? 'เพิ่ม/แก้/ลบรายการแล้ว ระบบคำนวณร่างใหม่ให้เอง ตัวเลขในตารางอัปเดตทันที'
                : status === 'draft'
                ? 'ร่างนี้รันก่อนระบบคำนวณใหม่ให้เอง — กด "รันร่างใหม่" ครั้งเดียว จากนั้นแก้รายการแล้วตัวเลขอัปเดตเอง'
                : 'ส่งขออนุมัติแล้ว — แก้รายการแล้วต้องกด "รันร่างใหม่" และส่งขออนุมัติใหม่ ตัวเลขถึงจะอัปเดต'}
          </p>
        </div>
      )}

      {/* ช่วงวันที่ของงวด + เตือนสรุปเวลาก่อนสิ้นงวด (วันที่เหลือถูกนับล่วงหน้าว่ามาทำงาน) */}
      {rows.length > 0 && rows[0]!.period_start && rows[0]!.period_end && (
        <div className="card p-3 mb-3 text-xs text-gray-700 space-y-2">
          <div>
            งวดนี้นับวันที่ <strong>{thDate(rows[0]!.period_start)} – {thDate(rows[0]!.period_end)}</strong>
            · ค่าแรงรายวัน/รายชั่วโมงดึงจากข้อมูลพนักงาน · เงินเดือนรายเดือนดึงจากฐานเงินเดือน
          </div>
          {resummarized > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>สรุปเวลาของ {resummarized} คนถูกทำใหม่หลังรันร่างเงินเดือน — ตัวเลขในตารางยังเป็นของชุดเก่า กด "รันร่างใหม่" ก่อนส่งอนุมัติ</div>
            </div>
          )}
          {stale && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">สรุปเวลาของ {stale.count} คนทำไว้เมื่อ {thDate(stale.date)} ซึ่งยังไม่สิ้นงวด — ตัวเลขนี้เป็นร่างคร่าว ๆ ยังไม่ใช่ยอดจ่ายจริง</div>
                <div>
                  ระบบไม่ได้ล็อก รันร่างดูได้ตลอด · รายวัน: นับเฉพาะวันที่สแกนจริง + ลาได้ค่าจ้าง + นักขัตฤกษ์ ถึง {thDate(stale.date)} — วันหลังจากนั้นยังไม่นับ ค่าแรงจึงต่ำกว่ายอดสิ้นงวด
                  · รายเดือน: วันหลังจากนั้นถือว่ามาทำงาน (จ่ายตามฐาน ไม่กระทบเงิน) — หลังสิ้นงวดให้ไปที่แท็บ "สรุปเวลา" กด "เริ่มสรุปเวลา" แล้วกลับมากด "รันร่างใหม่" จึงจะได้ยอดจริง
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rows table */}
      {rows.length > 0 && (
        <div className="card overflow-hidden mb-4">
          <div className="px-3 py-2 bg-gray-50 border-b flex flex-wrap items-center gap-2">
            {TYPE_FILTERS.map((f) => {
              const n = f.key === 'all' ? groupRows.length : groupRows.filter((r) => typeOf(r) === f.key).length;
              if (f.key !== 'all' && n === 0) return null;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setTypeFilter(f.key)}
                  className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap ${
                    typeFilter === f.key ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:border-brand-400'
                  }`}
                >
                  {f.label} {n}
                </button>
              );
            })}
            <span className="text-[11px] text-gray-500 ml-auto">แตะที่พนักงานเพื่อดูวิธีคิด / แก้ไขรายการ</span>
          </div>
          {/* fb#69 — กรองตามแผนก/ตำแหน่ง (ดึงจากข้อมูลพนักงาน) เพื่อปรับค่าคอม/รายการหักทีละกลุ่ม */}
          <DeptPositionFilter rows={rows} dept={deptFilter} setDept={setDeptFilter} position={positionFilter} setPosition={setPositionFilter} />
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] leading-tight">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-2 py-1.5 text-xs font-medium align-bottom">พนักงาน</th>
                  <th className="text-left px-2 py-1.5 text-xs font-medium align-bottom">ประเภท /<wbr />อัตรา</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight" title="นับจากวันที่มีสแกนเข้างานในงวดนี้ (นับสด)">สแกน (วัน)</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight" title="วันมาทำงานตามสรุปเวลา — ตัวที่ใช้คิดเงิน">มาทำงาน (วัน)</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight" title="รายวัน: จำนวนวันที่คูณค่าแรง (มาทำงาน + ลาได้ค่าจ้าง + วันหยุดนักขัตฤกษ์) — ไม่รวมวันหยุดประจำสัปดาห์ เพราะลูกจ้างรายวันไม่ได้ค่าจ้างวันนั้นตามกฎหมาย">วันที่จ่าย</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">ค่าจ้าง/<wbr />เงินเดือน</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">รายได้รวม</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">ภาษี</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">ปกส.</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">หักรวม</th>
                  <th className="text-right px-2 py-1.5 text-xs font-medium align-bottom leading-tight">โอนสุทธิ</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shownRows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-3 py-6 text-center text-gray-500 text-sm">ไม่มีพนักงานตรงกับตัวกรองนี้</td>
                  </tr>
                )}
                {shownRows.map((r) => {
                  const v = payBasisView(r);
                  const fmtDays = (n: number | null) => (n == null ? '—' : n.toLocaleString('th-TH', { maximumFractionDigits: 3 }));
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setDetailFor(r.emp_code)}
                      className="cursor-pointer hover:bg-brand-50/60 transition-colors"
                    >
                      <td className="px-2 py-1.5">
                        <div>{empName.get(r.emp_code) ?? r.emp_code}</div>
                        <div className="text-[11px] text-gray-400 font-mono whitespace-nowrap">
                          {r.emp_code}
                          {r.bank ? <span className="font-sans"> · {r.bank}{r.bank_account_masked ? ` ••${r.bank_account_masked}` : ''}</span> : null}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-xs whitespace-nowrap">
                        <div className="text-gray-800">{v.typeLabel}</div>
                        <div className="text-gray-500">{v.rateLabel ?? '—'}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{v.scanDays.toLocaleString('th-TH')}</td>
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap tabular-nums ${v.scanMismatch ? 'text-amber-700 font-semibold' : ''}`}>
                        {fmtDays(v.workedDays)}
                        {v.scanMismatch && (
                          <span title="ไม่ตรงกับวันที่สแกนเข้างาน — แตะเพื่อดูรายละเอียด">
                            <AlertTriangle className="inline w-3 h-3 ml-1 -mt-0.5" />
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-gray-700">{v.type === 'daily' ? fmtDays(v.paidDays) : '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{r.base_pay.toLocaleString('th-TH')}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{r.gross.toLocaleString('th-TH')}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-gray-600">
                        {r.tax_amount.toLocaleString('th-TH')}
                        {r.pay_basis?.statutory_mode === 'wht_pnd3' && (
                          <span className="block text-[10px] text-violet-700 whitespace-nowrap">ภ.ง.ด.3 {r.pay_basis.wht_rate_pct ?? 3}%</span>
                        )}
                        {r.pay_basis?.statutory_mode === 'no_deduction' && (
                          <span className="block text-[10px] text-violet-700 whitespace-nowrap">สิทธิ์พิเศษ ไม่หัก</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-gray-600">{r.sso_amount.toLocaleString('th-TH')}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-red-700">{r.deductions_total.toLocaleString('th-TH')}</td>
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap tabular-nums font-semibold ${r.net === 0 ? 'text-amber-700' : ''}`}>
                        {r.net.toLocaleString('th-TH')}
                        {r.net === 0 && <AlertTriangle className="inline w-3 h-3 ml-1 -mt-0.5" />}
                      </td>
                      <td className="pr-2 text-gray-300"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <tr>
                  <td className="px-2 py-1.5 whitespace-nowrap">รวม {shownRows.length} คน</td>
                  <td colSpan={4} className="px-2 py-1.5 text-gray-300">—</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{sumBase(shownRows).toLocaleString('th-TH')}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{sum(shownRows, 'gross').toLocaleString('th-TH')}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{sum(shownRows, 'tax_amount').toLocaleString('th-TH')}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums">{sum(shownRows, 'sso_amount').toLocaleString('th-TH')}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-red-700">{sum(shownRows, 'deductions_total').toLocaleString('th-TH')}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-brand-900">{sum(shownRows, 'net').toLocaleString('th-TH')}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Preview details */}
      {preview && (
        <div className="card p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">เปรียบเทียบกับงวด {preview.prev_period}</div>
            <div className="text-xs text-gray-500">
              ผิดปกติ {preview.employees.filter((e) => e.anomalies.length).length} รายการ
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs mb-2">
            <Total label="รวมรายได้" cur={preview.totals.gross_sum} prev={preview.prev_totals.gross_sum} />
            <Total label="รวมหัก"   cur={preview.totals.deductions_sum} prev={preview.prev_totals.deductions_sum} />
            <Total label="รวมสุทธิ"  cur={preview.totals.net_sum} prev={preview.prev_totals.net_sum} />
          </div>
          {preview.employees.filter((e) => e.anomalies.length).length > 0 && (
            <div className="bg-amber-50 rounded p-2 mt-2 text-xs space-y-1">
              <div className="flex items-center gap-1 font-medium text-amber-800">
                <AlertTriangle className="w-3 h-3" /> รายการที่ผิดปกติ
              </div>
              {preview.employees
                .filter((e) => e.anomalies.length)
                .map((e) => (
                  <div key={e.emp_code} className="text-amber-900">
                    <span>{nameOf(e.emp_code)}</span> · สุทธิ {e.net.toLocaleString('th-TH')}
                    {e.delta_pct !== null && (
                      <span className="ml-1">
                        ({e.delta_pct > 0 ? '+' : ''}{e.delta_pct.toFixed(1)}% เทียบงวดก่อน)
                      </span>
                    )}
                    {e.anomalies.includes('net_zero') && <span className="ml-1 text-red-700">[สุทธิ=0]</span>}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Print/send the approval request — HR tool (owner sees it only in solo) */}
      {canPrintRequest && rows.length > 0 && status && status !== 'voided' && (
        <button onClick={handlePrint} className="btn-secondary w-full mb-2">
          <Printer className="w-4 h-4" /> พิมพ์ใบขออนุมัติ
        </button>
      )}

      {/* Bank-transfer file — HR+ (after approval; real account numbers) */}
      {rows.length > 0 && ['approved', 'locked', 'pdf_ready', 'sending', 'partial_sent', 'sent'].includes(status ?? '') && (
        <button onClick={handleDownloadBankFile} className="btn-secondary w-full mb-2">
          <Banknote className="w-4 h-4" /> ดาวน์โหลดไฟล์โอนธนาคาร (CSV)
        </button>
      )}

      {/* Stage actions */}
      <div className="space-y-2">
        {(status === null || status === 'draft') && (
          <>
            <label className="flex items-start gap-2.5 px-1 py-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={salaryOnlyRun}
                onChange={(e) => setSalaryOnlyRun(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-600"
              />
              <span className="text-sm text-gray-600">
                ข้ามการลงเวลา/ลา/OT งวดนี้ — จ่ายเต็มตามฐานเงินเดือน
                <span className="block text-xs text-gray-400">คิดเฉพาะ เงินเดือน เงินเพิ่ม รายการหัก และภาษี/ประกันสังคม</span>
              </span>
            </label>
            <button
              onClick={handleRun}
              disabled={runPayroll.isPending || autoRerunning}
              className="btn-primary w-full"
            >
              <Play className="w-4 h-4" /> {status === 'draft' ? 'รันร่างใหม่' : 'รันร่าง'}
            </button>
          </>
        )}

        {status === 'draft' && (
          <button
            onClick={handlePreview}
            disabled={previewPayroll.isPending || autoRerunning}
            className="btn-cta w-full"
          >
            <Eye className="w-4 h-4" /> {isOwner ? 'ดูตัวอย่าง + เทียบงวดก่อน' : 'ส่งขออนุมัติ'}
          </button>
        )}

        {status === 'previewed' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleApprove}
              disabled={approvePayroll.isPending}
              className="btn-cta"
            >
              <CheckCircle2 className="w-4 h-4" /> อนุมัติ
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={rejectPayroll.isPending}
              className="btn-secondary text-red-700 border-red-200"
            >
              <XCircle className="w-4 h-4" /> ปฏิเสธ
            </button>
          </div>
        )}

        {status === 'approved' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleLock}
              disabled={lockPayroll.isPending}
              className="btn-primary"
            >
              <Lock className="w-4 h-4" /> ล็อกงวด
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={rejectPayroll.isPending}
              className="btn-secondary text-amber-700 border-amber-200"
            >
              <RotateCw className="w-4 h-4" /> ถอยกลับเป็นร่าง
            </button>
          </div>
        )}

        {(status === 'locked' || status === 'pdf_ready' || status === 'partial_sent') && (
          <button
            onClick={handleSend}
            disabled={sendSlips.isPending}
            className="btn-cta w-full"
          >
            <Send className="w-4 h-4" /> ส่งสลิปทางไลน์
          </button>
        )}

        {status === 'sent' && (
          <div className="text-center text-sm text-green-700 py-2">
            ✓ ส่งสลิปครบทุกคนแล้ว
          </div>
        )}

        {/* ส่งสลิปทางอีเมล — เลือกคน/แผนกก่อนส่ง (งวดที่ปิดแล้ว) */}
        {(status === 'locked' || status === 'pdf_ready' || status === 'sending' || status === 'partial_sent' || status === 'sent') && (
          <Link to={`/owner/payroll/email-slips?period=${period}`} className="btn-secondary w-full inline-flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" /> ส่งสลิปทางอีเมล (เลือกคนได้)
          </Link>
        )}
        {(status === 'sending' || status === 'partial_sent' || status === 'sent') && (
          <p className="text-xs text-center text-gray-500">ส่งทางไลน์แล้ว · อีเมลยังไม่ส่งเองอัตโนมัติ ต้องกด "ส่งสลิปทางอีเมล" แล้วเลือกคนที่จะส่ง</p>
        )}

        {/* ปลดล็อกงวด — เฉพาะเจ้าของ, งวดที่ปิด/ส่งไปแล้ว (B6) */}
        {isOwner && (status === 'locked' || status === 'pdf_ready' || status === 'sending' || status === 'partial_sent' || status === 'sent') && (
          <button
            onClick={() => setUnlockOpen(true)}
            disabled={unlockPayroll.isPending}
            className="btn-secondary w-full text-amber-700 border-amber-200"
          >
            <Unlock className="w-4 h-4" /> ปลดล็อกงวด
          </button>
        )}
      </div>

      {/* Deliveries breakdown — shows after lock onwards */}
      {showDeliveries && deliveries.data && deliveries.data.length > 0 && (
        <div className="card p-3 mt-4">
          <div className="font-semibold mb-2">การส่งสลิปรายคน</div>
          <div className="space-y-1">
            {deliveries.data.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs border-b border-gray-50 last:border-0 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <DeliveryIcon s={d.push_status} />
                  <span className="font-mono">{d.emp_code}</span>
                  {d.push_attempts > 0 && (
                    <span className="text-gray-400">·{d.push_attempts} ครั้ง</span>
                  )}
                  {d.push_last_error && (
                    <span className="text-red-600 truncate" title={d.push_last_error}>
                      {d.push_last_error}
                    </span>
                  )}
                </div>
                {(d.push_status === 'failed' || d.push_status === 'blocked') && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await retryDelivery.mutateAsync(d.id);
                        if (r.error) toast.error(`ยังส่งไม่สำเร็จ: ${r.error}`);
                        else toast.success('ส่งซ้ำสำเร็จ');
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
                      }
                    }}
                    disabled={retryDelivery.isPending}
                    className="btn-secondary text-xs py-1 px-2"
                  >
                    <RotateCw className="w-3 h-3" /> retry
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-employee detail drawer — breakdown + this person's adjustments */}
      <PersonDetailModal
        period={period}
        empCode={detailFor}
        name={detailFor ? nameOf(detailFor) : ''}
        employeeType={rows.find((r) => r.emp_code === detailFor)?.employee_type ?? null}
        hourlyRate={rows.find((r) => r.emp_code === detailFor)?.daily_rate ?? null}
        row={rows.find((r) => r.emp_code === detailFor) ?? null}
        editable={status === 'draft' || status === 'previewed'}
        autoRerun={canAutoRerun}
        onChanged={() => void rerunAfterEdit()}
        entries={(dataEntries.data ?? []).filter((e) => e.emp_code === detailFor)}
        onClose={() => setDetailFor(null)}
        onAdd={() => detailFor && openAdjustment(detailFor)}
        onDelete={async (id) => {
          if (await confirm({ title: 'ลบรายการนี้?', danger: true, confirmText: 'ลบ' })) {
            await removeEntry.mutateAsync(id);
            void rerunAfterEdit();
          }
        }}
      />

      {/* Adjustment modal — replaces the old 4× window.prompt flow */}
      <Modal open={adjFor !== null} onClose={closeAdjustmentModal} title={`${editingEntryId ? 'แก้ไข' : 'เพิ่ม'}รายการปรับ — ${adjFor ? nameOf(adjFor) : ''}`}>
        <div className="space-y-4">
          {/* รายการปรับที่มีอยู่แล้วของคนนี้ในงวดนี้ — กดแก้ไข/ลบได้เลย */}
          {(() => {
            const existing = (dataEntries.data ?? []).filter(
              (e) => e.emp_code === adjFor && (e.entry_type === 'adjustment_add' || e.entry_type === 'adjustment_deduct'),
            );
            if (existing.length === 0) return null;
            return (
              <div>
                <label className="label">รายการปรับที่มีอยู่แล้วงวดนี้</label>
                <div className="space-y-1 rounded-lg border border-gray-200 p-2">
                  {existing.map((e) => (
                    <div
                      key={e.id}
                      className={`flex items-center justify-between text-xs py-1 px-1.5 rounded ${editingEntryId === e.id ? 'bg-brand-50' : ''}`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        <span className={e.entry_type === 'adjustment_add' ? 'text-green-700' : 'text-red-700'}>
                          {e.entry_type === 'adjustment_add' ? '+' : '−'}{e.amount.toLocaleString('th-TH')}
                        </span>
                        {' '}({e.adjustment_label})
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" onClick={() => startEditAdjustment(e)} className="px-2 py-0.5 text-brand-700 hover:underline">
                          แก้ไข
                        </button>
                        <button type="button" onClick={() => deleteAdjustmentEntry(e.id)} className="p-1 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {editingEntryId && (
            <p className="text-xs text-brand-700 bg-brand-50 rounded-lg p-2">
              กำลังแก้ไขรายการเดิม — บันทึกแล้วจะทับยอด/ชื่อของรายการนี้ (ไม่สร้างรายการใหม่ซ้อน)
            </p>
          )}
          <div>
            <label className="label">ทิศทาง</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setAdjDir('add')} className={`w-full ${adjDir === 'add' ? 'btn-cta' : 'btn-secondary'}`}>
                <Plus className="w-4 h-4" /> เงินเพิ่ม
              </button>
              <button type="button" onClick={() => setAdjDir('deduct')} className={`w-full ${adjDir === 'deduct' ? 'btn-danger' : 'btn-secondary'}`}>
                <Minus className="w-4 h-4" /> หักเงิน
              </button>
            </div>
          </div>
          {adjDir === 'add' && (
            <div>
              <label className="label">ประเภท</label>
              <div className="space-y-1.5 text-sm">
                {([
                  ['bonus', 'โบนัส / เงินเพิ่มพิเศษ', 'เสียภาษี · ไม่เข้าประกันสังคม'],
                  ['wage_correction', 'ปรับค่าจ้างตกหล่น', 'เสียภาษี + ปกส. + PF'],
                  ['reimbursement', 'เบิกคืน / ไม่ใช่เงินได้', 'ไม่เสียภาษี'],
                ] as const).map(([val, label, sub]) => (
                  <label key={val} className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${adjPreset === val ? 'border-brand-500 bg-brand-50' : 'border-gray-200'}`}>
                    <input type="radio" name="adj-preset" checked={adjPreset === val} onChange={() => setAdjPreset(val)} className="mt-0.5 accent-brand-600" />
                    <span><b>{label}</b><br /><span className="text-xs text-gray-500">{sub}</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* fb#133 — ชื่อที่ใช้บ่อย กดเลือกแทนพิมพ์ · เฉพาะชุดตั้งต้น (ไม่ดึงชื่อที่เคยพิมพ์ — astra: ชื่อที่ไม่โดน
              conflict regex ของนำเข้าไฟล์ชีตจะทำให้เงินก้อนเดียวกันเข้าซ้ำโดยไม่เตือน) */}
          {(() => {
            const presets = ADJ_LABEL_CHOICES[adjDir];
            const choices = presets.map((p) => p.label);
            return (
              <div>
                <label className="label">เลือกรายการ</label>
                <div className="flex flex-wrap gap-1.5">
                  {choices.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => {
                        setAdjLabel(l);
                        const p = presets.find((x) => x.label === l)?.preset;
                        if (adjDir === 'add' && p) setAdjPreset(p);
                      }}
                      className={`rounded-full border px-3 py-1.5 text-xs whitespace-nowrap ${adjLabel.trim() === l ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">ไม่มีในนี้ พิมพ์ชื่อเองในช่องด้านล่างได้</p>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">ชื่อรายการ</label>
              <input className="input" value={adjLabel} onChange={(e) => setAdjLabel(e.target.value)} placeholder="เช่น โบนัสพิเศษ Q1" />
              <p className="text-[11px] text-gray-500 mt-1">บันทึกชื่อเดิมซ้ำ = แก้ไขรายการเดิม (ไม่เพิ่มรายการซ้อน)</p>
            </div>
            <div>
              <label className="label">จำนวน (บาท)</label>
              <input className="input" inputMode="numeric" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={closeAdjustmentModal}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitAdjustment} disabled={upsertAdj.isPending}>
              {editingEntryId ? 'บันทึกการแก้ไข' : 'บันทึกรายการ'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reject / revert reason modal — same backend action, status-aware copy */}
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title={status === 'approved' ? 'ถอยกลับเป็นร่าง' : 'ปฏิเสธเงินเดือน'}>
        <div className="space-y-3">
          {status === 'approved' && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
              งวดนี้อนุมัติแล้วแต่ยังไม่ได้ล็อก — ถอยกลับเป็นร่างเพื่อแก้ไขแล้วรันร่าง/ขออนุมัติใหม่ได้
            </p>
          )}
          <div>
            <label className="label">{status === 'approved' ? 'เหตุผลที่ถอยกลับ' : 'เหตุผลที่ปฏิเสธ'}</label>
            <textarea className="input" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="ระบุเหตุผล…" />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setRejectOpen(false)}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitReject} disabled={rejectPayroll.isPending}>
              {status === 'approved' ? 'ถอยกลับเป็นร่าง' : 'ปฏิเสธ'}
            </button>
          </div>
        </div>
      </Modal>

      {/* fb#145 — ล็อกงวดทั้งที่มีเรื่องค้าง: แสดงรายการ + ใครโดน แล้วใส่เหตุผลยิงซ้ำ */}
      <Modal
        open={lockAck !== null}
        onClose={() => { if (!lockPayroll.isPending) { setLockAck(null); setLockAckReason(''); } }}
        title={`ยืนยันล็อกงวด ${lockAck?.period ?? period} ทั้งที่มีเรื่องค้าง`}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            ยังมีเรื่องค้างที่กระทบรอบเงินเดือนงวดนี้ — ล็อกได้ แต่ต้องระบุเหตุผล
            (ระบบบันทึกไว้ในประวัติการใช้งาน) ถ้าอยากแก้ก่อน ดูรายละเอียดได้ที่{' '}
            <Link to="/owner/cycle#close" className="text-brand-600 underline">ความพร้อมปิดรอบ</Link>
          </p>
          {(lockAck?.items ?? []).length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {(lockAck?.items ?? []).map((it) => {
                const raw = (it.detail as { emp_codes?: unknown } | undefined)?.emp_codes;
                const codes = Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : [];
                return (
                  <div key={it.key} className="text-sm bg-amber-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 min-w-0">{it.label}</span>
                      <span className="font-semibold tabular-nums">{it.count}</span>
                    </div>
                    {codes.length > 0 && (
                      <div className="mt-1 text-xs text-amber-900/80 break-words">
                        กระทบ: {codes.slice(0, 12).join(', ')}
                        {codes.length > 12 ? ` และอีก ${codes.length - 12} คน` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <label className="label">เหตุผลที่ล็อกงวดทั้งที่มีเรื่องค้าง</label>
            <textarea
              className="input"
              rows={3}
              value={lockAckReason}
              onChange={(e) => setLockAckReason(e.target.value)}
              placeholder="เช่น ตรวจแล้ว รายการที่ค้างไม่กระทบยอดงวดนี้ / นำไปคิดงวดหน้า"
            />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => { setLockAck(null); setLockAckReason(''); }}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitLockAck} disabled={lockPayroll.isPending}>
              {lockPayroll.isPending ? 'กำลังดำเนินการ…' : 'ยืนยันล็อกงวด'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ปลดล็อกงวด — เจ้าของเท่านั้น + ต้องพิมพ์เหตุผล (B6) */}
      <Modal
        open={unlockOpen}
        onClose={() => { if (!unlockPayroll.isPending) { setUnlockOpen(false); setUnlockReason(''); } }}
        title={`ปลดล็อกงวด ${period}`}
      >
        <div className="space-y-3">
          <p className="text-xs text-red-700 bg-red-50 rounded-lg p-2">
            งวดนี้ปิดแล้ว{(status === 'sent' || status === 'partial_sent') ? 'และส่งสลิปไปแล้วบางส่วนหรือทั้งหมด' : ''} —
            ปลดล็อกจะถอยงวดนี้กลับไปสถานะ &quot;อนุมัติแล้ว&quot; เพื่อแก้ไขข้อมูลแล้วทำร่าง/ล็อกใหม่
            {(status === 'sent' || status === 'partial_sent') && ' หากพนักงานได้รับสลิปไปแล้ว จะยังถือยอดเดิมอยู่ในมือ ต้องส่งสลิปใหม่หลังล็อกงวดอีกครั้ง'}
          </p>
          <div>
            <label className="label">เหตุผลที่ปลดล็อก</label>
            <textarea
              className="input"
              rows={3}
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="เช่น กรอกชั่วโมง OT ผิด ต้องแก้แล้วคำนวณใหม่"
            />
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => { setUnlockOpen(false); setUnlockReason(''); }}>ยกเลิก</button>
            <button className="btn-danger flex-1" onClick={submitUnlock} disabled={unlockPayroll.isPending}>
              {unlockPayroll.isPending ? 'กำลังดำเนินการ…' : 'ยืนยันปลดล็อก'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Standalone page kept for legacy /owner/payroll (now redirects via hub). */
export function OwnerPayroll() {
  return (
    <AdminLayout title="ทำเงินเดือน">
      <OwnerPayrollContent />
    </AdminLayout>
  );
}

/**
 * Per-employee breakdown drawer. Reads the itemized earnings/deductions
 * snapshot from the last Run Draft (calculation_log.line_items) so the owner
 * sees how the number was reached, plus this period's adjustments with
 * inline add/delete. Draft periods re-run automatically after an edit (fb#630);
 * previewed periods still need "รันร่างใหม่".
 */
function PersonDetailModal({
  period, empCode, name, employeeType, hourlyRate, row, editable, autoRerun, onChanged, entries, onClose, onAdd, onDelete,
}: {
  period: string;
  empCode: string | null;
  name: string;
  employeeType: 'monthly' | 'daily' | 'hourly' | null;
  hourlyRate: number | null;
  row: PayrollRow | null;
  editable: boolean;
  autoRerun: boolean;
  onChanged: () => void;
  entries: DataEntryRow[];
  onClose: () => void;
  onAdd: () => void;
  onDelete: (id: string) => Promise<void>;
}) {
  const detail = usePayrollRunDetail(period, empCode);
  const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const d = detail.data;

  // รายชั่วโมง: ช่องกรอกชั่วโมงงวดนี้ (แอดมินกรอกเอง → ค่าจ้าง = เรท × ชั่วโมง)
  const setHours = useSetHoursWorked();
  const hoursToast = useToast();
  const hoursEntry = entries.find((e) => e.entry_type === 'hours_worked');
  const [hoursInput, setHoursInput] = useState('');
  useEffect(() => {
    setHoursInput(hoursEntry?.qty != null ? String(hoursEntry.qty) : '');
  }, [hoursEntry?.qty, empCode]);
  async function saveHours() {
    if (!empCode) return;
    const h = Number(hoursInput);
    if (!Number.isFinite(h) || h < 0) { hoursToast.error('กรอกจำนวนชั่วโมงให้ถูกต้อง'); return; }
    try {
      await setHours.mutateAsync({ period, emp_code: empCode, hours: h });
      if (autoRerun) {
        hoursToast.success('บันทึกชั่วโมงแล้ว — กำลังคำนวณร่างใหม่');
        onChanged();
      } else {
        hoursToast.success('บันทึกชั่วโมงแล้ว — กด "รันร่างใหม่" ตัวเลขถึงจะอัปเดต');
      }
    } catch (e) {
      hoursToast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }
  return (
    <Modal open={empCode !== null} onClose={onClose} title={`รายละเอียดเงินเดือน — ${name}`}>
      {detail.isLoading ? (
        <p className="text-sm text-gray-400 py-6 text-center">กำลังโหลด…</p>
      ) : !d ? (
        <p className="text-sm text-gray-400 py-6 text-center">ยังไม่มีข้อมูลคนนี้ในงวดนี้ — กรุณารันร่างก่อน</p>
      ) : (
        <div className="space-y-4">
          {row && <PayBasisBox row={row} />}
          {/* Earnings */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายได้</div>
            <div className="space-y-0.5 text-sm">
              {d.earnings.length === 0 && <div className="text-gray-400 italic text-xs">— ไม่มีรายการ</div>}
              {d.earnings.map((e, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-700">{e.label}</span>
                  <span className="text-green-700">{baht(e.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 mt-1 font-medium">
                <span>รวมรายได้</span><span>{baht(d.gross)}</span>
              </div>
            </div>
          </div>
          {/* Deductions */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายการหัก</div>
            <div className="space-y-0.5 text-sm">
              {d.deductions.length === 0 && <div className="text-gray-400 italic text-xs">— ไม่มีรายการ</div>}
              {d.deductions.map((e, i) => {
                // fb#143 — บรรทัดยกไปงวดหน้าเคยโชว์ "+ยอด" สีเขียวในกล่องรายการหัก ลูกค้าอ่านไม่ออก.
                // เพดาน 10% ถูกตัดแล้ว — เหลือเกิดเฉพาะเงินไม่พอหัก (หรือร่างเก่าที่ยังไม่คำนวณใหม่)
                if (e.amount < 0 && e.label.startsWith('ยกไปงวดหน้า')) {
                  return (
                    <div key={i}>
                      <div className="flex justify-between">
                        <span className="text-gray-700">เลื่อนไปหักงวดหน้า</span>
                        <span className="text-gray-500">ไม่หักงวดนี้ {baht(-e.amount)}</span>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-snug">
                        ยอดนี้ยังไม่ถูกหักในงวดนี้ · ถ้าเป็นร่างที่คำนวณก่อน 29 ก.ย. 2569 กดคำนวณใหม่แล้วจะหักเต็ม
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={i} className="flex justify-between">
                    <span className="text-gray-700">{e.label}</span>
                    {/* #10 — negative = §76 credit (เงินสุทธิขั้นต่ำ) → show as +, not −− */}
                    <span className={e.amount < 0 ? 'text-green-700' : 'text-red-700'}>{e.amount < 0 ? `+${baht(-e.amount)}` : `−${baht(e.amount)}`}</span>
                  </div>
                );
              })}
              <div className="flex justify-between border-t pt-1 mt-1 font-medium">
                {/* #12 — use the line total (incl. หักลา/ขาด + §76 carry/credit lines) so the
                    displayed total matches the itemized rows. deductions_total is net of leave/absent. */}
                <span>รวมหัก</span><span className="text-red-700">−{baht(d.deductions_line_total)}</span>
              </div>
            </div>
          </div>
          {/* Net */}
          <div className="flex justify-between items-center bg-brand-50 rounded-lg px-3 py-2">
            <span className="font-semibold text-brand-900">เงินสุทธิ</span>
            <span className="text-lg font-bold text-brand-900">{baht(d.net)}</span>
          </div>
          {/* รายชั่วโมง: กรอกชั่วโมงทำงานงวดนี้ → ค่าจ้าง = เรท × ชั่วโมง */}
          {employeeType === 'hourly' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="text-xs font-semibold text-amber-800">ชั่วโมงทำงานงวดนี้ (พนักงานรายชั่วโมง)</div>
              <div className="flex items-center gap-2">
                <input
                  type="number" step="0.5" min="0"
                  className="input flex-1"
                  value={hoursInput}
                  onChange={(e) => setHoursInput(e.target.value)}
                  placeholder={row?.pay_basis?.hours_source === 'scan' && row.pay_basis.hours_worked != null ? `จากสแกน ${row.pay_basis.hours_worked}` : 'เช่น 80'}
                  disabled={!editable}
                />
                <span className="text-xs text-gray-500 shrink-0">ชม.</span>
                {editable && (
                  <button onClick={saveHours} disabled={setHours.isPending} className="btn-secondary text-sm shrink-0">
                    {setHours.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
                  </button>
                )}
              </div>
              {hourlyRate != null && hourlyRate > 0 && Number(hoursInput) > 0 && (
                <p className="text-[11px] text-amber-700">
                  เรท ฿{hourlyRate.toLocaleString('th-TH')}/ชม. × {Number(hoursInput) || 0} ชม. ={' '}
                  <strong>฿{((Number(hoursInput) || 0) * hourlyRate).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  {editable && ' · บันทึกแล้วกด "รันร่างใหม่"'}
                </p>
              )}
              {/* fb#135 — ลูกค้าไม่รู้ว่ากล่องนี้คือที่กำหนดชั่วโมงเอง (ช่องว่างขึ้น "× 0 ชม. = ฿0") → บอกค่าที่ใช้อยู่ตอนนี้ */}
              {!(Number(hoursInput) > 0) && row?.pay_basis?.hours_source === 'scan' && row.pay_basis.hours_worked != null && (
                <p className="text-[11px] text-amber-800">
                  ตอนนี้ใช้ชั่วโมงจากสแกน <strong>{row.pay_basis.hours_worked.toLocaleString('th-TH')} ชม.</strong> —
                  ถ้าชั่วโมงที่ตกลงกันไม่เท่านี้ กรอกจำนวนชั่วโมงที่จะจ่ายในช่องด้านบน กดบันทึก แล้วกด "รันร่างใหม่"
                </p>
              )}
              {/* fb#439 — สวิตช์ดึงจากสแกนปิดอยู่แต่สแกนมี: บอกเลขที่สแกนได้ + ที่เปิดสวิตช์ (เดิมขึ้นแค่ "ยังไม่มีชั่วโมง" ลูกค้าคิดว่าระบบไม่เก็บเวลาเข้างาน) */}
              {!(Number(hoursInput) > 0) && row?.pay_basis?.hours_source === 'none' && (row.pay_basis.scan_hours_available ?? 0) > 0 && (
                <p className="text-[11px] text-red-700">
                  สแกนเข้า-ออกในงวดนี้รวม <strong>{(row.pay_basis.scan_hours_available ?? 0).toLocaleString('th-TH')} ชม.</strong> ({row.pay_basis.scan_days_counted ?? 0} วัน)
                  แต่ร่างนี้ยังไม่ได้นำมาคิด เพราะตอนรันร่าง สวิตช์ "รายชั่วโมง: ดึงชั่วโมงจากการสแกน" ปิดอยู่ —
                  ตรวจ/เปิดที่ <Link to="/owner/wage#paycycle" className="underline font-medium">ค่าจ้าง → รอบจ่ายเงินเดือน</Link> แล้วกด "รันร่างใหม่" (เปิดแล้วระบบจะบังคับรันใหม่ก่อนอนุมัติ) · หรือกรอกชั่วโมงเองในช่องด้านบน
                </p>
              )}
              <p className="text-[11px] text-gray-500">
                {row?.pay_basis?.hours_source === 'none'
                  ? 'ร่างนี้รันตอนสวิตช์ "ดึงชั่วโมงจากการสแกน" ปิด (ค่าจ้าง → รอบจ่ายเงินเดือน) · ถ้าเปิดสวิตช์แล้ว กด "รันร่างใหม่" จะใช้ชั่วโมงจากสแกน · กรอกตัวเลข = ใช้ค่าที่กรอก'
                  : 'ถ้าบริษัทเปิด "ดึงชั่วโมงจากการสแกน" (ค่าจ้าง → รอบจ่ายเงินเดือน): เว้นว่างหรือกรอก 0 = ใช้ชั่วโมงจากสแกน · กรอกตัวเลข = ใช้ค่าที่กรอกแทน'}
              </p>
            </div>
          )}
          {/* YTD */}
          {d.ytd && (
            <div className="grid grid-cols-3 gap-2 text-center">
              {([['รายได้สะสม', d.ytd.gross], ['ภาษีสะสม', d.ytd.tax], ['สุทธิสะสม', d.ytd.net]] as const).map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded p-1.5">
                  <div className="text-[10px] text-gray-500">{label}</div>
                  <div className="text-xs font-semibold">{val != null ? baht(val) : '—'}</div>
                </div>
              ))}
            </div>
          )}
          {/* This period's adjustments */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">รายการปรับงวดนี้</div>
            {entries.length === 0 ? (
              <p className="text-xs text-gray-400 italic">ยังไม่มีรายการปรับ</p>
            ) : (
              <div className="space-y-1">
                {entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                    <span className="min-w-0 flex-1">
                      {e.entry_type === 'variable_allowance' ? (
                        <span>เบี้ยเลี้ยงผันแปร · จำนวน {e.qty}</span>
                      ) : (
                        <span className={e.entry_type === 'adjustment_add' ? 'text-green-700' : 'text-red-700'}>
                          {e.entry_type === 'adjustment_add' ? '+' : '−'}
                          {e.amount.toLocaleString('th-TH')} ({e.adjustment_label})
                        </span>
                      )}
                    </span>
                    {editable && (
                      <button onClick={() => onDelete(e.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {editable ? (
            <>
              <button onClick={onAdd} className="btn-secondary w-full">
                <Plus className="w-4 h-4" /> เพิ่มรายการปรับ
              </button>
              <p className="text-[11px] text-amber-700 text-center">
                {autoRerun
                  ? 'แก้รายการแล้วระบบคำนวณร่างใหม่ให้เอง'
                  : 'แก้รายการแล้วต้องกด “รันร่างใหม่” ตัวเลขถึงจะอัปเดต'}
              </p>
            </>
          ) : (
            <p className="text-[11px] text-gray-400 text-center">งวดนี้ปิดแก้ไขแล้ว (เลยขั้นตอนตัวอย่าง)</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** วิธีคิดค่าจ้างฐาน + วันทำงานเทียบสแกน — ให้ HR ตรวจทานก่อนส่งอนุมัติ */
export function PayBasisBox({ row }: { row: PayrollRow }) {
  const v = payBasisView(row);
  const d = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 3 });
  const s = row.summary;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5">
      <div className="flex justify-between">
        <span className="font-semibold text-gray-700">วิธีคิดค่าจ้าง · {v.typeLabel}</span>
        <span className="text-gray-600">{v.rateLabel ?? '—'}</span>
      </div>
      {v.formula && <div className="text-gray-800">{v.formula}</div>}
      {v.type === 'monthly' && <div className="text-gray-600">รายเดือนจ่ายตามฐานเงินเดือน หักเฉพาะวันขาด/ลาไม่รับค่าจ้าง</div>}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 border-t border-gray-200">
        <span className="text-gray-500">สแกนเข้างานจริง (วันทำงาน)</span><span className="text-right">{v.scanDays} วัน</span>
        {v.offdayScanDays > 0 && <><span className="text-gray-500">สแกนในวันหยุด (คิดเป็นค่าทำงานวันหยุด)</span><span className="text-right">{v.offdayScanDays} วัน</span></>}
        <span className="text-gray-500">มาทำงาน (ใช้คิด)</span>
        <span className={`text-right ${v.scanMismatch ? 'text-amber-700 font-semibold' : ''}`}>{v.workedDays == null ? '—' : `${d(v.workedDays)} วัน`}</span>
        {s && <><span className="text-gray-500">ลาได้ค่าจ้าง</span><span className="text-right">{d(s.paid_leave_days)} วัน</span></>}
        {s && v.type === 'daily' && !v.legacyHoliday ? (
          <>
            <span className="text-gray-500">วันหยุดประจำสัปดาห์ (ไม่ได้ค่าจ้าง)</span><span className="text-right">{d(v.unpaidDayoffDays)} วัน</span>
            <span className="text-gray-500">วันหยุดนักขัตฤกษ์{v.onlyAttended ? ' (ไม่ได้ค่าจ้าง — จ่ายเฉพาะวันมาทำงาน)' : v.holidayUnpaid ? ` (พาร์ทไทม์ได้ค่าจ้างเฉพาะวันที่มาทำ ${v.paidHolidayDays} วัน)` : ' (ได้ค่าจ้าง)'}</span><span className="text-right">{d(v.publicHolidayDays)} วัน</span>
          </>
        ) : s && <><span className="text-gray-500">วันหยุด (ประจำสัปดาห์ + นักขัตฤกษ์)</span><span className="text-right">{d(s.holiday_days)} วัน</span></>}
        {s && <><span className="text-gray-500">ขาด / ลาไม่รับค่าจ้าง</span><span className="text-right">{d(s.absent_days)} / {d(s.unpaid_leave_days)} วัน</span></>}
      </div>
      {v.scanMismatch && (
        <div className="flex items-start gap-1.5 text-amber-800 pt-1">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>วันมาทำงานที่ใช้คิดไม่ตรงกับวันที่สแกนเข้างาน — ถ้าสรุปเวลาทำก่อนสิ้นงวด ให้สรุปใหม่แล้วรันร่างใหม่ · ดูรายวันได้ที่ ประวัติเข้างานรายคน</span>
        </div>
      )}
      {v.legacyHoliday && (
        <div className="flex items-start gap-1.5 text-amber-800 pt-1">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>สรุปเวลา/ร่างนี้ทำก่อนระบบแยกวันหยุดประจำสัปดาห์ออกจากวันที่จ่าย — รายวันจึงยังถูกนับวันหยุดประจำสัปดาห์เป็นวันจ่าย ให้กด "เริ่มสรุปเวลา" แล้ว "รันร่างใหม่"</span>
        </div>
      )}
      {v.estimated && <div className="text-gray-400">รันร่างก่อนมีช่องนี้ ตัวเลขวันมาจากสรุปเวลาปัจจุบัน — รันร่างใหม่เพื่อให้ตรงกับตอนคิดเงิน</div>}
    </div>
  );
}

function sumBase(rows: PayrollRow[]): number {
  return Math.round(rows.reduce((a, r) => a + (r.base_pay ?? 0), 0) * 100) / 100;
}

function sum(rows: PayrollRow[], key: 'gross' | 'tax_amount' | 'sso_amount' | 'welfare_fund_amount' | 'deductions_total' | 'net'): number {
  return rows.reduce((t, r) => t + (r[key] ?? 0), 0);
}

function Chip({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`rounded-lg p-2 ${accent ? 'bg-brand-600 text-white' : 'bg-white border border-gray-100'}`}>
      <div className={`text-[10px] ${accent ? 'text-brand-100' : 'text-gray-500'}`}>{label}</div>
      <div className="text-sm font-semibold">{value.toLocaleString('th-TH')}</div>
    </div>
  );
}

function DeliveryIcon({ s }: { s: 'pending' | 'sent' | 'failed' | 'blocked' }) {
  if (s === 'sent') return <MailCheck className="w-3.5 h-3.5 text-green-700" />;
  if (s === 'failed') return <MailX className="w-3.5 h-3.5 text-red-600" />;
  if (s === 'blocked') return <MailWarning className="w-3.5 h-3.5 text-amber-700" />;
  return <Send className="w-3.5 h-3.5 text-gray-400" />;
}

function Total({ label, cur, prev }: { label: string; cur: number; prev: number }) {
  const delta = prev !== 0 ? ((cur - prev) / prev) * 100 : null;
  const color = delta !== null && Math.abs(delta) >= 20 ? 'text-amber-700' : 'text-gray-600';
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-[10px] text-gray-500">{label}</div>
      <div className="font-semibold">{cur.toLocaleString('th-TH')}</div>
      <div className={`text-[10px] ${color}`}>
        {prev > 0 ? `prev ${prev.toLocaleString('th-TH')}` : ''}
        {delta !== null && (
          <span className="ml-1">({delta > 0 ? '+' : ''}{delta.toFixed(1)}%)</span>
        )}
      </div>
    </div>
  );
}

// fb#69 — กรองตามแผนก/ตำแหน่ง (ค่าจากข้อมูลพนักงานในงวดนี้) — HR ปรับค่าคอม/รายการหักทีละกลุ่มได้
// '' = ทั้งหมด · NONE = คนที่ยังไม่ระบุในข้อมูลพนักงาน · fb#555 ใช้ร่วมกับหน้าซ้อมทำเงินเดือน
type GroupedRow = { department?: string | null; position?: string | null };
const NONE = '__none__';
const groupKey = (v: string | null | undefined) => (v && v.trim() ? v.trim() : NONE);
function distinctGroups(
  rows: GroupedRow[],
  pick: (r: GroupedRow) => string | null | undefined,
  cmp: (a: string, b: string) => number = (a, b) => a.localeCompare(b, 'th'),
) {
  const m = new Map<string, number>();
  for (const r of rows) {
    const k = groupKey(pick(r));
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  const named = [...m.entries()].filter(([k]) => k !== NONE).sort((a, b) => cmp(a[0], b[0]));
  return m.has(NONE) ? [...named, [NONE, m.get(NONE)!] as [string, number]] : named;
}
export function filterByGroup<T extends GroupedRow>(rows: T[], dept: string, position: string): T[] {
  return rows.filter((r) => (!dept || groupKey(r.department) === dept) && (!position || groupKey(r.position) === position));
}

export function DeptPositionFilter({ rows, dept: deptFilter, setDept: setDeptFilter, position: positionFilter, setPosition: setPositionFilter }: {
  rows: GroupedRow[];
  dept: string;
  setDept: (v: string) => void;
  position: string;
  setPosition: (v: string) => void;
}) {
  const deptOptions = distinctGroups(rows, (r) => r.department);
  // fb#245 — ตำแหน่ง: ชื่อขึ้นต้นอังกฤษอยู่บน ตามด้วยไทย · ในกลุ่มเรียงสั้น→ยาว (ไม่นับสระบน/ล่าง/วรรณยุกต์) แล้วตามตัวอักษร
  const positionOptions = distinctGroups(rows, (r) => r.position, (a, b) => {
    const latin = (s: string) => (/^[A-Za-z0-9]/.test(s) ? 0 : 1);
    const width = (s: string) => s.replace(/[\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]/g, '').length;
    return latin(a) - latin(b) || width(a) - width(b) || a.localeCompare(b, 'th');
  });
  return (
    <div className="px-3 py-2 bg-white border-b flex flex-wrap items-center gap-2 text-xs">
      <label className="flex items-center gap-1.5">
        <span className="text-gray-600">แผนก</span>
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white min-h-[36px]"
          aria-label="กรองตามแผนก"
        >
          <option value="">ทุกแผนก ({rows.length})</option>
          {deptOptions.map(([k, n]) => (
            <option key={k} value={k}>{k === NONE ? 'ยังไม่ระบุแผนก' : k} ({n})</option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1.5">
        <span className="text-gray-600">ตำแหน่ง</span>
        <select
          value={positionFilter}
          onChange={(e) => setPositionFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white min-h-[36px]"
          aria-label="กรองตามตำแหน่ง"
        >
          <option value="">ทุกตำแหน่ง ({rows.length})</option>
          {positionOptions.map(([k, n]) => (
            <option key={k} value={k}>{k === NONE ? 'ยังไม่ระบุตำแหน่ง' : k} ({n})</option>
          ))}
        </select>
      </label>
      {(deptFilter || positionFilter) && (
        <button
          type="button"
          onClick={() => { setDeptFilter(''); setPositionFilter(''); }}
          className="px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:border-brand-400"
        >
          ล้างตัวกรอง
        </button>
      )}
      <span className="text-[11px] text-gray-500">แผนก/ตำแหน่งดึงจากข้อมูลพนักงาน — แก้ได้ที่เมนูพนักงาน</span>
    </div>
  );
}
